import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import {
  Calendar,
  Crown,
  Edit,
  Plus,
  Trash2,
  Trophy,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import "./App.css";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Item Component for Drag and Drop
function SortableItem({ id, player, team, position, dnf, dnfCause, onDnfChange, onDnfCauseChange }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-md transition-shadow ${
        isDragging ? "shadow-lg" : ""
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex items-center gap-3 cursor-grab flex-1"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          dnf ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
        }`}>
          {dnf ? "DNF" : (position || "?")}
        </div>
        <div>
          <div className="font-medium">{player.name}</div>
          <Badge variant="outline" className="text-xs">
            {team.name}
          </Badge>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={dnf}
            onChange={(e) => onDnfChange(id, e.target.checked)}
            className="w-4 h-4"
          />
          DNF
        </label>
        {dnf && (
          <input
            type="text"
            placeholder="DNF cause"
            value={dnfCause || ""}
            onChange={(e) => onDnfCauseChange(id, e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="border rounded px-2 py-1 text-sm w-40"
          />
        )}
      </div>
    </div>
  );
}

// Auth context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("formula_d_auth") === "true";
  });

  const login = async (username, password) => {
    try {
      const credentials = btoa(`${username}:${password}`);
      const response = await axios.post(
        `${API}/auth/login`,
        {},
        {
          headers: { Authorization: `Basic ${credentials}` },
        }
      );

      if (response.status === 200) {
        setIsAuthenticated(true);
        localStorage.setItem("formula_d_auth", "true");
        localStorage.setItem("formula_d_credentials", credentials);
        return true;
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Invalid credentials");
      return false;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("formula_d_auth");
    localStorage.removeItem("formula_d_credentials");
  };

  const getAuthHeaders = () => {
    const credentials = localStorage.getItem("formula_d_credentials");
    return credentials ? { Authorization: `Basic ${credentials}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, getAuthHeaders }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return React.useContext(AuthContext);
}

// Login Component
function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const success = await login(username, password);
    if (!success) {
      setPassword("");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto mb-4 p-4 bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center">
            <Crown className="w-8 h-8" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            Formula D Admin
          </CardTitle>
          <CardDescription className="text-gray-600">
            Racing Championship Management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="login-username-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Dashboard Component
function Dashboard() {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [races, setRaces] = useState([]);
  const [driverStandings, setDriverStandings] = useState([]);
  const [constructorStandings, setConstructorStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { logout, getAuthHeaders } = useAuth();

  // Fetch all data
  const fetchData = async () => {
    try {
      const [teamsRes, playersRes, racesRes, driversRes, constructorsRes] =
        await Promise.all([
          axios.get(`${API}/teams`),
          axios.get(`${API}/players`),
          axios.get(`${API}/races`),
          axios.get(`${API}/standings/drivers`),
          axios.get(`${API}/standings/constructors`),
        ]);

      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
      setRaces(racesRes.data);
      setDriverStandings(driversRes.data);
      setConstructorStandings(constructorsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Formula D Championship...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-red-600 text-white rounded-lg">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Formula D Championship
                </h1>
                <p className="text-sm text-gray-500">
                  Racing Management System
                </p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="logout-button"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="standings" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white border shadow-sm">
            <TabsTrigger
              value="standings"
              className="flex items-center gap-2"
              data-testid="standings-tab"
            >
              <Trophy className="w-4 h-4" />
              Standings
            </TabsTrigger>
            <TabsTrigger
              value="teams"
              className="flex items-center gap-2"
              data-testid="teams-tab"
            >
              <Users className="w-4 h-4" />
              Teams
            </TabsTrigger>
            <TabsTrigger
              value="players"
              className="flex items-center gap-2"
              data-testid="players-tab"
            >
              <Users className="w-4 h-4" />
              Players
            </TabsTrigger>
            <TabsTrigger
              value="races"
              className="flex items-center gap-2"
              data-testid="races-tab"
            >
              <Calendar className="w-4 h-4" />
              Races
            </TabsTrigger>
          </TabsList>

          {/* Standings Tab */}
          <TabsContent value="standings" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Driver Standings */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <Trophy className="w-5 h-5" />
                    Driver Championship
                  </CardTitle>
                  <CardDescription>Individual driver standings</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pos</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Points</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driverStandings.map((standing, index) => (
                        <TableRow
                          key={standing.player.id}
                          data-testid={`driver-standing-${index + 1}`}
                        >
                          <TableCell className="font-bold">
                            {index + 1}
                            {index === 0 && (
                              <Crown className="w-4 h-4 text-yellow-500 inline ml-1" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {standing.player.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {standing.team.name}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-bold text-red-600">
                            {standing.total_points}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Constructor Standings */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Users className="w-5 h-5" />
                    Constructor Championship
                  </CardTitle>
                  <CardDescription>Team standings</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pos</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Points</TableHead>
                        <TableHead>Drivers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {constructorStandings.map((standing, index) => (
                        <TableRow
                          key={standing.team.id}
                          data-testid={`constructor-standing-${index + 1}`}
                        >
                          <TableCell className="font-bold">
                            {index + 1}
                            {index === 0 && (
                              <Crown className="w-4 h-4 text-yellow-500 inline ml-1" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {standing.team.name}
                          </TableCell>
                          <TableCell className="font-bold text-blue-600">
                            {standing.total_points}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {standing.players.map((p) => p.name).join(", ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <TeamsManagement
              teams={teams}
              setTeams={setTeams}
              players={players}
              fetchData={fetchData}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

          {/* Players Tab */}
          <TabsContent value="players">
            <PlayersManagement
              players={players}
              setPlayers={setPlayers}
              teams={teams}
              fetchData={fetchData}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

          {/* Races Tab */}
          <TabsContent value="races">
            <RacesManagement
              races={races}
              setRaces={setRaces}
              players={players}
              teams={teams}
              fetchData={fetchData}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Teams Management Component
function TeamsManagement({
  teams,
  setTeams,
  players,
  fetchData,
  getAuthHeaders,
}) {
  const [newTeamName, setNewTeamName] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      await axios.post(
        `${API}/teams`,
        { name: newTeamName },
        {
          headers: getAuthHeaders(),
        }
      );

      toast.success("Team created successfully");
      setNewTeamName("");
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create team");
    }
  };

  const handleUpdateTeam = async (teamId, newName) => {
    try {
      await axios.put(
        `${API}/teams/${teamId}`,
        { name: newName },
        {
          headers: getAuthHeaders(),
        }
      );

      toast.success("Team updated successfully");
      setEditingTeamId(null);
      setEditingTeamName("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update team");
    }
  };

  const handleStartEdit = (teamId, currentName) => {
    setEditingTeamId(teamId);
    setEditingTeamName(currentName);
  };

  const handleCancelEdit = () => {
    setEditingTeamId(null);
    setEditingTeamName("");
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}`, {
        headers: getAuthHeaders(),
      });

      toast.success("Team deleted successfully");
      fetchData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Cannot delete team with players");
      } else {
        toast.error("Failed to delete team");
      }
    }
  };

  const handleSaveEdit = (teamId) => {
    if (editingTeamName.trim()) {
      handleUpdateTeam(teamId, editingTeamName.trim());
    }
  };

  const getTeamPlayers = (teamId) => {
    return players.filter((p) => p.team_id === teamId);
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Users className="w-5 h-5" />
              Teams Management
            </CardTitle>
            <CardDescription>Manage Formula D racing teams</CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-team-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Team
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Team</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTeam} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="teamName">Team Name</Label>
                  <Input
                    id="teamName"
                    placeholder="Enter team name"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    data-testid="team-name-input"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-create-team"
                  >
                    Create Team
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Team Name</TableHead>
              <TableHead>Players</TableHead>
              <TableHead>Player Count</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teams.map((team) => {
              const teamPlayers = getTeamPlayers(team.id);
              return (
                <TableRow key={team.id} data-testid={`team-row-${team.id}`}>
                  <TableCell className="font-medium">
                    {editingTeamId === team.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editingTeamName}
                          onChange={(e) => setEditingTeamName(e.target.value)}
                          className="h-8"
                          data-testid={`edit-team-name-${team.id}`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSaveEdit(team.id);
                            } else if (e.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSaveEdit(team.id)}
                          data-testid={`save-team-${team.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          data-testid={`cancel-team-${team.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{team.name}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEdit(team.id, team.name)}
                          data-testid={`edit-team-${team.id}`}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {teamPlayers.length > 0 ? (
                      <div className="space-y-1">
                        {teamPlayers.map((player) => (
                          <Badge
                            key={player.id}
                            variant="secondary"
                            className="mr-1"
                          >
                            {player.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 italic">No players</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        teamPlayers.length === 2 ? "default" : "destructive"
                      }
                    >
                      {teamPlayers.length}/2
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteTeam(team.id)}
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Players Management Component
function PlayersManagement({
  players,
  setPlayers,
  teams,
  fetchData,
  getAuthHeaders,
}) {
  const [newPlayer, setNewPlayer] = useState({ name: "", team_id: "" });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editingPlayerName, setEditingPlayerName] = useState("");

  const handleCreatePlayer = async (e) => {
    e.preventDefault();
    if (
      !newPlayer.name.trim() ||
      !newPlayer.team_id ||
      getAvailableTeams().length === 0
    )
      return;

    try {
      await axios.post(`${API}/players`, newPlayer, {
        headers: getAuthHeaders(),
      });

      toast.success("Player created successfully");
      setNewPlayer({ name: "", team_id: "" });
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create player");
    }
  };

  const handleUpdatePlayer = async (playerId, newName, teamId) => {
    try {
      await axios.put(
        `${API}/players/${playerId}`,
        { name: newName, team_id: teamId },
        {
          headers: getAuthHeaders(),
        }
      );

      toast.success("Player updated successfully");
      setEditingPlayerId(null);
      setEditingPlayerName("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update player");
    }
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      await axios.delete(`${API}/players/${playerId}`, {
        headers: getAuthHeaders(),
      });

      toast.success("Player deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete player");
    }
  };

  const handleStartEdit = (playerId, currentName) => {
    setEditingPlayerId(playerId);
    setEditingPlayerName(currentName);
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setEditingPlayerName("");
  };

  const handleSaveEdit = (playerId, teamId) => {
    if (editingPlayerName.trim()) {
      handleUpdatePlayer(playerId, editingPlayerName.trim(), teamId);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team ? team.name : "Unknown Team";
  };

  const getTeamPlayerCount = (teamId) => {
    return players.filter((p) => p.team_id === teamId).length;
  };

  const isTeamFull = (teamId) => {
    return getTeamPlayerCount(teamId) >= 2;
  };

  const getAvailableTeams = () => {
    return teams.filter((team) => !isTeamFull(team.id));
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Users className="w-5 h-5" />
              Players Management
            </CardTitle>
            <CardDescription>Manage Formula D drivers</CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-player-button"
                disabled={getAvailableTeams().length === 0}
                title={
                  getAvailableTeams().length === 0
                    ? "No teams available - all teams are full"
                    : ""
                }
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Player
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Player</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePlayer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="playerName">Player Name</Label>
                  <Input
                    id="playerName"
                    placeholder="Enter player name"
                    value={newPlayer.name}
                    onChange={(e) =>
                      setNewPlayer({ ...newPlayer, name: e.target.value })
                    }
                    data-testid="player-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team">Team</Label>
                  {getAvailableTeams().length > 0 ? (
                    <Select
                      value={newPlayer.team_id}
                      onValueChange={(value) =>
                        setNewPlayer({ ...newPlayer, team_id: value })
                      }
                      data-testid="player-team-select"
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableTeams().map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name} ({getTeamPlayerCount(team.id)}/2)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 bg-gray-50 border rounded-md text-sm text-gray-600">
                      No teams available. All teams are full (2/2 players).
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-create-player"
                    disabled={getAvailableTeams().length === 0}
                  >
                    Add Player
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Player Name</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {players.map((player) => (
              <TableRow key={player.id} data-testid={`player-row-${player.id}`}>
                <TableCell className="font-medium">
                  {editingPlayerId === player.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editingPlayerName}
                        onChange={(e) => setEditingPlayerName(e.target.value)}
                        className="h-8"
                        data-testid={`edit-player-name-${player.id}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveEdit(player.id, player.team_id);
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleSaveEdit(player.id, player.team_id)
                        }
                        data-testid={`save-player-${player.id}`}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEdit}
                        data-testid={`cancel-player-${player.id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{player.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(player.id, player.name)}
                        data-testid={`edit-player-${player.id}`}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{getTeamName(player.team_id)}</Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeletePlayer(player.id)}
                    data-testid={`delete-player-${player.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Races Management Component
function RacesManagement({
  races,
  setRaces,
  players,
  teams,
  fetchData,
  getAuthHeaders,
}) {
  const navigate = useNavigate();
  const [newRace, setNewRace] = useState({ name: "", date: "" });
  const [showCreateRaceDialog, setShowCreateRaceDialog] = useState(false);

  const handleCreateRace = async (e) => {
    e.preventDefault();
    if (!newRace.name.trim() || !newRace.date) return;

    try {
      await axios.post(`${API}/races`, newRace, {
        headers: getAuthHeaders(),
      });

      toast.success("Race created successfully");
      setNewRace({ name: "", date: "" });
      setShowCreateRaceDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create race");
    }
  };

  const handleDeleteRace = async (raceId, e) => {
    e.stopPropagation(); // Prevent row click navigation
    try {
      await axios.delete(`${API}/races/${raceId}`, {
        headers: getAuthHeaders(),
      });

      toast.success("Race deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete race");
    }
  };

  const isRaceInPast = (raceDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const race = new Date(raceDate);
    race.setHours(0, 0, 0, 0);
    return race < today;
  };

  const handleRaceClick = (raceId) => {
    navigate(`/races/${raceId}/edit`);
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Calendar className="w-5 h-5" />
              Races Management
            </CardTitle>
            <CardDescription>Manage races and results</CardDescription>
          </div>
          <Dialog
            open={showCreateRaceDialog}
            onOpenChange={setShowCreateRaceDialog}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-race-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Race
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Race</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateRace} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="raceName">Race Name</Label>
                  <Input
                    id="raceName"
                    placeholder="Enter race name"
                    value={newRace.name}
                    onChange={(e) =>
                      setNewRace({ ...newRace, name: e.target.value })
                    }
                    data-testid="race-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="raceDate">Race Date</Label>
                  <Input
                    id="raceDate"
                    type="date"
                    value={newRace.date}
                    onChange={(e) =>
                      setNewRace({ ...newRace, date: e.target.value })
                    }
                    data-testid="race-date-input"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateRaceDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="confirm-create-race"
                  >
                    Create Race
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Race Name</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {races.map((race) => (
              <TableRow 
                key={race.id} 
                data-testid={`race-row-${race.id}`}
                onClick={() => handleRaceClick(race.id)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <TableCell className="font-medium">{race.name}</TableCell>
                <TableCell>
                  {new Date(race.date).toLocaleDateString()}
                </TableCell>
                <TableCell className="space-x-2">
                  {!isRaceInPast(race.date) && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => handleDeleteRace(race.id, e)}
                      data-testid={`delete-race-${race.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Race Edit Page Component
function RaceEditPage() {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [race, setRace] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [raceResults, setRaceResults] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch race data and preload placements
  useEffect(() => {
    const fetchRaceData = async () => {
      try {
        setLoading(true);
        const [raceWithResultsRes, playersRes, teamsRes] = await Promise.all([
          axios.get(`${API}/races/${raceId}/results`),
          axios.get(`${API}/players`),
          axios.get(`${API}/teams`),
        ]);

        const raceData = raceWithResultsRes.data;
        setRace(raceData.race);
        setPlayers(playersRes.data);
        setTeams(teamsRes.data);

        // Preload placement data
        const existingResults = raceData.results || [];
        
        // Create a map of existing results by player_id
        const resultsMap = {};
        existingResults.forEach(result => {
          resultsMap[result.player_id] = result;
        });

        // Initialize results with all players
        const initialResults = playersRes.data.map((player) => {
          const existingResult = resultsMap[player.id];
          if (existingResult) {
            return {
              id: player.id,
              player_id: player.id,
              position: existingResult.position,
              dnf: existingResult.dnf,
              dnf_cause: existingResult.dnf_cause || "",
            };
          }
          return {
            id: player.id,
            player_id: player.id,
            position: null,
            dnf: false,
            dnf_cause: "",
          };
        });

        // Separate DNF and non-DNF players
        const nonDnfResults = [];
        const dnfResults = [];
        
        initialResults.forEach((result) => {
          if (result.dnf) {
            dnfResults.push(result);
          } else {
            nonDnfResults.push(result);
          }
        });
        
        // Sort non-DNF players by position
        nonDnfResults.sort((a, b) => {
          if (a.position === null && b.position === null) return 0;
          if (a.position === null) return 1;
          if (b.position === null) return -1;
          return a.position - b.position;
        });
        
        // Sort DNF players by their previous position (if any) to maintain order
        dnfResults.sort((a, b) => {
          if (a.position === null && b.position === null) return 0;
          if (a.position === null) return 1;
          if (b.position === null) return -1;
          return a.position - b.position;
        });
        
        // Recalculate positions for non-DNF players only
        const updatedNonDnf = nonDnfResults.map((result, index) => ({
          ...result,
          position: index + 1
        }));
        
        // Keep DNF players with null positions
        const updatedDnf = dnfResults.map((result) => ({
          ...result,
          position: null
        }));
        
        // Combine: non-DNF players first, then DNF players
        setRaceResults([...updatedNonDnf, ...updatedDnf]);
      } catch (error) {
        console.error("Error fetching race data:", error);
        toast.error("Failed to load race data");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchRaceData();
  }, [raceId, navigate, getAuthHeaders]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setRaceResults((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Separate DNF and non-DNF players
        const nonDnfResults = [];
        const dnfResults = [];
        
        newItems.forEach((result) => {
          if (result.dnf) {
            dnfResults.push(result);
          } else {
            nonDnfResults.push(result);
          }
        });
        
        // Recalculate positions for non-DNF players only
        const updatedNonDnf = nonDnfResults.map((result, index) => ({
          ...result,
          position: index + 1
        }));
        
        // Keep DNF players with null positions but maintain their order
        const updatedDnf = dnfResults.map((result) => ({
          ...result,
          position: null
        }));
        
        // Return non-DNF players first, then DNF players
        return [...updatedNonDnf, ...updatedDnf];
      });
    }
  };

  const handleDnfChange = (playerId, isDnf) => {
    setRaceResults((prev) => {
      const newResults = [...prev];
      
      // Find the index of the player being changed
      const playerIndex = newResults.findIndex((r) => r.player_id === playerId);
      if (playerIndex === -1) return prev;
      
      // Update the DNF status
      newResults[playerIndex] = {
        ...newResults[playerIndex],
        dnf: isDnf,
        position: isDnf ? null : newResults[playerIndex].position,
        dnf_cause: isDnf ? (newResults[playerIndex].dnf_cause || "") : ""
      };
      
      // Separate DNF and non-DNF players
      const nonDnfResults = [];
      const dnfResults = [];
      
      newResults.forEach((result) => {
        if (result.dnf) {
          dnfResults.push(result);
        } else {
          nonDnfResults.push(result);
        }
      });
      
      // Recalculate positions for non-DNF players
      const updatedNonDnf = nonDnfResults.map((result, index) => ({
        ...result,
        position: index + 1
      }));
      
      // Keep DNF players with null positions but maintain their order
      const updatedDnf = dnfResults.map((result) => ({
        ...result,
        position: null
      }));
      
      // Return non-DNF players first, then DNF players
      return [...updatedNonDnf, ...updatedDnf];
    });
  };

  const handleDnfCauseChange = (playerId, cause) => {
    setRaceResults((prev) =>
      prev.map((r) =>
        r.player_id === playerId ? { ...r, dnf_cause: cause } : r
      )
    );
  };

  const handleSubmitResults = async () => {
    try {
      await axios.post(
        `${API}/races/${raceId}/results`,
        {
          race_id: raceId,
          results: raceResults,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      toast.success("Race results saved successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to save results");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading race data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="mr-2"
              >
                ← Back
              </Button>
              <div className="p-2 bg-red-600 text-white rounded-lg">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Edit Race Results
                </h1>
                <p className="text-sm text-gray-500">{race?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Trophy className="w-5 h-5" />
              Race Results: {race?.name}
            </CardTitle>
            <CardDescription>
              Drag and drop players to set their finishing positions. Check DNF
              for drivers who did not finish.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={raceResults.map((result) => result.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {raceResults.map((result) => {
                      const player = players.find(
                        (p) => p.id === result.player_id
                      );
                      const team = teams.find((t) => t.id === player?.team_id);

                      if (!player || !team) return null;

                      return (
                        <SortableItem
                          key={result.id}
                          id={result.id}
                          player={player}
                          team={team}
                          position={result.position}
                          dnf={result.dnf}
                          dnfCause={result.dnf_cause}
                          onDnfChange={handleDnfChange}
                          onDnfCauseChange={handleDnfCauseChange}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitResults}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="save-results-button"
                >
                  Save Results
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function App() {
  // Get basename from environment or default to '/'
  const basename = process.env.REACT_APP_BASENAME || "/";

  return (
    <AuthProvider>
      <BrowserRouter basename={basename}>
        <AuthChecker />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

function AuthChecker() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" />}
      />
      <Route
        path="/"
        element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
      />
      <Route
        path="/races/:raceId/edit"
        element={isAuthenticated ? <RaceEditPage /> : <Navigate to="/login" />}
      />
    </Routes>
  );
}

export default App;
